import { useEffect, useRef, useState } from "react";
import { Minus, Plus, Sparkles } from "lucide-react";
import { RARITIES, fmt } from "./economy";
import { t } from "@/i18n";

// ─────────────────────────────────────────────────────────────
// CONFIRMATION D'ACHAT
// Un seul composant pour parcelles, décors et coffres.
//
// NOUVEAU : compteur de quantité. Il démarre TOUJOURS à 1 — acheter
// une seule fois doit rester le geste par défaut, sans réflexion —
// et se plafonne à ce que le solde permet réellement, pour qu'on ne
// puisse pas composer un panier impossible puis se faire refuser.
// Il ne s'affiche que là où il a du sens (décor achetable) : pas pour
// une parcelle, qui est unique par définition.
// ─────────────────────────────────────────────────────────────
export function ConfirmPurchase({
  open,
  title,
  subtitle,
  price,
  balance,
  preview,
  cta,
  allowQuantity = false,
  maxQuantity = 99,
  // Prix qui n'est PAS un simple multiple : chaque exemplaire coûte
  // plus cher que le précédent (habitants). Quand elle est fournie,
  // cette fonction fait autorité sur le total affiché ET sur le
  // plafond du compteur — sinon on proposerait ×5 à un joueur qui n'a
  // les moyens que de trois.
  totalFor = null,
  onConfirm,
  onCancel,
}) {
  const [qty, setQty] = useState(1);

  // Chaque nouvelle confirmation repart de 1.
  useEffect(() => {
    if (open) setQty(1);
  }, [open, title, price]);

  if (!open) return null;

  const unit = price || 0;
  let affordableMax;
  if (totalFor) {
    affordableMax = 0;
    while (
      affordableMax < maxQuantity &&
      totalFor(affordableMax + 1) <= balance
    )
      affordableMax++;
  } else {
    affordableMax = unit > 0 ? Math.floor(balance / unit) : maxQuantity;
  }
  const cap = Math.max(1, Math.min(maxQuantity, affordableMax || 1));
  const q = allowQuantity ? Math.min(qty, Math.max(1, cap)) : 1;
  const total = totalFor ? totalFor(q) : unit * q;
  const affordable = balance >= total;
  const after = balance - total;

  const step = (d) => setQty((n) => Math.max(1, Math.min(cap, n + d)));

  return (
    <div
      className="fixed inset-0 z-[95] grid place-items-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-xs rounded-3xl bg-card border p-5 text-center"
        onClick={(e) => e.stopPropagation()}
        style={{ animation: "oasisPop .28s cubic-bezier(.2,1.3,.4,1)" }}
      >
        {preview && (
          <div className="mx-auto mb-3 grid place-items-center">{preview}</div>
        )}
        <h3 className="font-head text-lg font-bold leading-tight">{title}</h3>
        {subtitle && (
          <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
        )}

        {allowQuantity && (
          <div className="mt-4 flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => step(-1)}
              disabled={q <= 1}
              className="w-10 h-10 grid place-items-center rounded-2xl border disabled:opacity-30 hover:bg-muted transition"
            >
              <Minus size={16} />
            </button>
            <div className="min-w-[74px]">
              <div className="font-head text-2xl font-extrabold tabular-nums leading-none">
                ×{q}
              </div>
              <div className="text-[10px] text-muted-foreground mt-0.5">
                {cap > 1
                  ? `${t("feedback.jusqu_a")} ×${cap}`
                  : t("feedback.solde_limite")}
              </div>
            </div>
            <button
              type="button"
              onClick={() => step(1)}
              disabled={q >= cap}
              className="w-10 h-10 grid place-items-center rounded-2xl border disabled:opacity-30 hover:bg-muted transition"
            >
              <Plus size={16} />
            </button>
          </div>
        )}

        <div className="my-4 rounded-2xl bg-muted p-3 space-y-1.5 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">
              {q > 1
                ? totalFor
                  ? t("feedback.prix_exemplaires", { count: q })
                  : t("feedback.prix_unitaire", { price: fmt(unit), count: q })
                : t("feedback.prix")}
            </span>
            <strong className="tabular-nums">{fmt(total)} 🪙</strong>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">
              {t("feedback.apres_achat")}
            </span>
            <strong
              className={`tabular-nums ${affordable ? "" : "text-destructive"}`}
            >
              {fmt(after)} 🪙
            </strong>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 rounded-2xl border py-2.5 text-sm font-semibold hover:bg-muted transition"
          >
            {t("groupui.annuler")}
          </button>
          <button
            type="button"
            onClick={() => onConfirm(q)}
            disabled={!affordable}
            className="flex-1 rounded-2xl bg-amber-500 text-white py-2.5 text-sm font-bold disabled:opacity-40 hover:brightness-110 transition"
          >
            {affordable
              ? `${cta || t("feedback.acheter")}${q > 1 ? ` ×${q}` : ""}`
              : t("feedback.trop_cher")}
          </button>
        </div>
      </div>
      <style>{`@keyframes oasisPop{from{transform:scale(.85);opacity:0}to{transform:scale(1);opacity:1}}`}</style>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// OUVERTURE DE COFFRE — trois temps : secousse, éclat, révélation.
// ─────────────────────────────────────────────────────────────
export function BoxOpening({ box, result, onDone, SpriteComp }) {
  const [phase, setPhase] = useState("shake");
  const timers = useRef([]);

  useEffect(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [
      setTimeout(() => setPhase("burst"), 1400),
      setTimeout(() => setPhase("reveal"), 2150),
    ];
    return () => timers.current.forEach(clearTimeout);
  }, [box, result]);

  if (!result) return null;
  const r = RARITIES[result.rarity] || RARITIES.commun;
  const rare = r.order >= 2;

  return (
    <div className="fixed inset-0 z-[96] grid place-items-center bg-black/75 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-sm text-center">
        <div
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full pointer-events-none transition-all duration-700"
          style={{
            width: phase === "shake" ? 40 : 460,
            height: phase === "shake" ? 40 : 460,
            background: `radial-gradient(circle, ${r.color}cc 0%, ${r.color}22 45%, transparent 70%)`,
            opacity: phase === "shake" ? 0 : phase === "burst" ? 1 : 0.55,
            filter: "blur(6px)",
          }}
        />

        {phase !== "shake" && rare && (
          <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
            style={{
              width: 420,
              height: 420,
              animation: "oasisSpin 14s linear infinite",
            }}
          >
            {Array.from({ length: 12 }).map((_, i) => (
              <div
                key={i}
                className="absolute left-1/2 top-1/2 origin-left"
                style={{
                  width: 210,
                  height: 3,
                  transform: `rotate(${i * 30}deg)`,
                  background: `linear-gradient(90deg, ${r.color}aa, transparent)`,
                  opacity: 0.5,
                }}
              />
            ))}
          </div>
        )}

        {phase !== "reveal" ? (
          <div className="relative py-16">
            <div
              style={{
                animation:
                  phase === "shake"
                    ? "oasisShake .35s ease-in-out infinite"
                    : "oasisPopBig .5s ease-out",
              }}
            >
              <div
                className={`mx-auto w-28 h-28 rounded-3xl grid place-items-center bg-gradient-to-br ${box.accent} shadow-2xl`}
              >
                <Sparkles size={44} className="text-white/90" />
              </div>
            </div>
            <p className="relative mt-6 text-sm font-semibold text-white/90">
              {phase === "shake"
                ? t("feedback.coffre_ouvre")
                : r.label.toUpperCase()}
            </p>
          </div>
        ) : (
          <div
            className="relative rounded-3xl bg-card border p-6"
            style={{ animation: "oasisPop .4s cubic-bezier(.2,1.4,.4,1)" }}
          >
            <div className="text-xs uppercase tracking-wider text-muted-foreground">
              {box.name}
            </div>
            <div
              className={`mx-auto my-5 w-32 h-32 grid place-items-center rounded-3xl ring-4 ${r.ring} bg-background`}
            >
              {SpriteComp}
            </div>
            <span
              className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${r.chip}`}
            >
              {r.label}
            </span>
            <h3 className="font-head text-2xl font-extrabold mt-2">
              {result.item?.name}
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {result.isNew
                ? t("feedback.nouvelle_piece")
                : t("feedback.doublon_piece")}
            </p>
            <div className="flex gap-2 mt-5">
              <button
                type="button"
                onClick={() => onDone(false)}
                className="flex-1 rounded-2xl border py-2.5 font-semibold hover:bg-muted transition"
              >
                {t("tutorial.fermer")}
              </button>
              <button
                type="button"
                onClick={() => onDone(true)}
                className="flex-1 rounded-2xl bg-primary text-primary-foreground py-2.5 font-semibold hover:brightness-110 transition"
              >
                {t("rewards.poser")}
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes oasisShake {
          0%,100% { transform: translateX(0) rotate(0deg) }
          25% { transform: translateX(-6px) rotate(-4deg) }
          75% { transform: translateX(6px) rotate(4deg) }
        }
        @keyframes oasisPopBig { from { transform: scale(.7) } 60% { transform: scale(1.25) } to { transform: scale(1) } }
        @keyframes oasisPop { from { transform: scale(.85); opacity: 0 } to { transform: scale(1); opacity: 1 } }
        @keyframes oasisSpin { to { transform: rotate(360deg) } }
        @media (prefers-reduced-motion: reduce) {
          [style*="oasisShake"], [style*="oasisSpin"] { animation: none !important }
        }
      `}</style>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// CÉLÉBRATION D'ACHAT DE PARCELLE — gerbes de particules
// ─────────────────────────────────────────────────────────────
export function ChunkCelebration({ onDone }) {
  useEffect(() => {
    const id = setTimeout(onDone, 2200);
    return () => clearTimeout(id);
  }, [onDone]);

  const bursts = [
    { x: 32, y: 38, delay: 0, hue: "#f59e0b" },
    { x: 66, y: 30, delay: 250, hue: "#38bdf8" },
    { x: 50, y: 52, delay: 480, hue: "#a78bfa" },
  ];

  return (
    <div className="fixed inset-0 z-[94] pointer-events-none">
      {bursts.map((b, bi) =>
        Array.from({ length: 14 }).map((_, i) => {
          const a = (i / 14) * Math.PI * 2;
          return (
            <span
              key={`${bi}-${i}`}
              className="absolute rounded-full"
              style={{
                left: `${b.x}%`,
                top: `${b.y}%`,
                width: 7,
                height: 7,
                background: b.hue,
                animation: `oasisBurst 1.3s ease-out ${b.delay}ms forwards`,
                ["--dx"]: `${Math.cos(a) * (90 + (i % 4) * 26)}px`,
                ["--dy"]: `${Math.sin(a) * (90 + (i % 4) * 26)}px`,
              }}
            />
          );
        }),
      )}
      <div className="absolute inset-x-0 top-[26%] text-center">
        <div
          className="inline-block rounded-2xl bg-amber-500 text-white font-head font-extrabold px-5 py-2.5 shadow-2xl"
          style={{ animation: "oasisPop .45s cubic-bezier(.2,1.5,.4,1)" }}
        >
          {t("feedback.nouvelle_parcelle")}
        </div>
      </div>
      <style>{`
        @keyframes oasisBurst {
          0% { transform: translate(0,0) scale(1); opacity: 1 }
          100% { transform: translate(var(--dx), var(--dy)) scale(.2); opacity: 0 }
        }
        @keyframes oasisPop { from { transform: scale(.7); opacity: 0 } to { transform: scale(1); opacity: 1 } }
        @media (prefers-reduced-motion: reduce) { span, div { animation: none !important } }
      `}</style>
    </div>
  );
}
